import { getParentSize } from "../diagram/diagram-assets/diagram-positioning-state";

/**
    @description Draws a rounded rectangle using the current state of the canvas.
    * If you omit the last three params, it will draw a rectangle
    * outline with a 5 pixel border radius

    * @param {Number} x The top left x coordinate
    * @param {Number} y The top left y coordinate
    * @param {Number} width The width of the rectangle
    * @param {Number} height The height of the rectangle
    * @param {Number} [radius = 5] The corner radius; It can also be an object
    *                 to specify different radii for corners
    * @param {Number} [radius.tl = 0] Top left
    * @param {Number} [radius.tr = 0] Top right
    * @param {Number} [radius.br = 0] Bottom right
    * @param {Number} [radius.bl = 0] Bottom left
    * @param {Object} [styles] Styling optitons { fillStyle, strokeStyle, shadowBlur, shadowOffsetX, shadowOffsetY, lineWidth}.
    * @param {CanvasRenderingContext2D} ctx
   */
export function roundRect(x, y, width, height, radius, styles, ctx) {
    if (typeof radius === "undefined") {
        radius = 5;
    }
    if (typeof radius === "number") {
        radius = {
            tl: radius, tr: radius, br: radius, bl: radius
        };
    } else {
        const defaultRadius = {
            tl: 0, tr: 0, br: 0, bl: 0
        };
        for (const side in defaultRadius) {
            radius[side] = radius[side] || defaultRadius[side];
        }
    }
    if (styles.lineWidth) {
        ctx.lineWidth = styles.lineWidth;
    }
    ctx.beginPath();
    ctx.moveTo(x + radius.tl, y);
    ctx.lineTo(x + width - radius.tr, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius.tr);
    ctx.lineTo(x + width, y + height - radius.br);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius.br, y + height);
    ctx.lineTo(x + radius.bl, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius.bl);
    ctx.lineTo(x, y + radius.tl);
    ctx.quadraticCurveTo(x, y, x + radius.tl, y);

    ctx.closePath();
    fillElement(ctx, styles);
    strokeElement(ctx, styles);
    // if you set the shadow of a element, make sure to draw it
    // after drawing it, make sure you reset the ctx properties
    if (styles.shadowColor) {
        ctx.shadowColor = styles.shadowColor;
        if (styles.shadowBlur) ctx.shadowBlur = styles.shadowBlur;
        if (styles.shadowOffsetX) ctx.shadowOffsetX = styles.shadowOffsetX;
        if (styles.shadowOffsetY) ctx.shadowOffsetY = styles.shadowOffsetY;
    }
}
/**
 * @description Used to draw the diamond shape plugin
 * @param {Number} x - X point to start drawing the diamond
 * @param {Number} y - y point to start drawing the diamond
 * @param {Number} width - diamond width
 * @param {Number} height - diamond height
 * @param {Object} styles - Styling options {fillStyle, strokeStyle}
 * @param {CanvasRenderingContext2D} ctx
 */
export function diamondObject(x, y, width, height, styles, ctx) {
    // if values are not set just exit
    if (isNaN(x) || isNaN(y) || isNaN(width) || isNaN(height)) {
        console.error("INVALID PARAMS DIAMOND OBJECT ");
        return true;
    }
    ctx.beginPath();
    ctx.lineCap = "round";
    ctx.moveTo(x + width * 0.5, y);
    ctx.lineTo(x, y + height * 0.5);
    ctx.lineTo(x + width * 0.5, y + height);
    ctx.lineTo(x + width, y + height * 0.5);
    ctx.lineTo(x + width * 0.5, y);
    ctx.lineCap = "square";
    ctx.closePath();
    fillElement(ctx, styles);
    strokeElement(ctx, styles);
}
/**
 * @description Used to draw a triangle
 * @param {Number} x - X point to start drawing the triangle
 * @param {Number} y - y point to start drawing the triangle
 * @param {Number} width - triangle width
 * @param {Number} height - triangle height
 * @param {Object} styles - Styling options { fillStyle, strokeStyle, lineWidth,shadowColor, shadowBlur, shadowOffsetX, shadowOffsetY }
 * @param {CanvasRenderingContext2D} ctx
 * @param {String} position - Used in certain plugins to indicate it must be drawed in a right top corner of the given coordonates
 */
export function triangleObject(x, y, width, height, radius, styles, ctx, position) {
    if (typeof radius === "undefined") {
        radius = 5;
    }
    if (typeof radius === "number") {
        radius = {
            tl: radius, tr: radius, br: radius, bl: radius
        };
    } else {
        const defaultRadius = {
            tl: 0, tr: 0, br: 0, bl: 0
        };
        for (const side in defaultRadius) {
            radius[side] = radius[side] || defaultRadius[side];
        }
    }
    if (styles.lineWidth) {
        ctx.lineWidth = styles.lineWidth;
    }
    if (position && position == "rightTop") {
        ctx.beginPath();
        ctx.moveTo(x + width * 2 / 3, y);
        ctx.quadraticCurveTo(x + width * 2 / 3, y + height * 1 / 3, x + width * 2 / 3, y + height * 1 / 3);
        ctx.lineTo(x + width * 2 / 3, y + height * 1 / 3);
        ctx.lineTo(x + width, y + height * 1 / 3);
    } else {
        ctx.beginPath();
        ctx.moveTo(x + width * 2 / 3, y);
        ctx.lineTo(x + width - radius.tr, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius.tr);
        ctx.lineTo(x + width, y + height * 1 / 3);
    }

    ctx.closePath();
    fillElement(ctx, styles);
    strokeElement(ctx, styles);
    if (styles.shadowColor) {
        ctx.shadowColor = styles.shadowColor;
        if (styles.shadowBlur) ctx.shadowBlur = styles.shadowBlur;
        if (styles.shadowOffsetX) ctx.shadowOffsetX = styles.shadowOffsetX;
        if (styles.shadowOffsetY) ctx.shadowOffsetY = styles.shadowOffsetY;
    }
}
/**
 * @description Used to draw a triangle
 * @param {Number} x - X point to start drawing the triangle
 * @param {Number} y - y point to start drawing the triangle
 * @param {Number} radius - triangle width
 * @param {Object} styles - Styling options {fillStyle, strokeStyle}
 * @param {CanvasRenderingContext2D} ctx - Canvas Context
 */
export function circleObject(x, y, radius, styles, ctx) {
    const borderLine = styles && styles.lineWidth ? styles.lineWidth : 0;
    ctx.beginPath();
    ctx.arc(x, y, radius - borderLine, 0, Math.PI * 2, true);
    ctx.closePath();
    if (styles.lineWidth) {
        ctx.lineWidth = styles.lineWidth;
    }
    fillElement(ctx, styles);
    strokeElement(ctx, styles);
}

export function drawIconRecurrence(x, y, radius, styles, ctx, recurrenceOff, isDisabled) {
    const defautStyle = styles ? { ...styles } : {};
    if (styles.lineWidth) {
        ctx.lineWidth = styles.lineWidth;
    }

    ctx.beginPath();
    ctx.arc(x, y, radius, 0, Math.PI * 2, true);
    ctx.moveTo(x, y);
    ctx.lineTo(x, y - 5);

    ctx.moveTo(x - 3, y - radius - 3);
    ctx.lineTo(x + 3, y - radius - 3);

    ctx.moveTo(x + radius - 2, y - radius + 3);
    ctx.lineTo(x + radius, y - radius + 1);

    ctx.moveTo(x + 3, y + 1);
    ctx.lineTo(x + 3, y + 1);
    strokeElement(ctx, styles);
    ctx.closePath();

    if (recurrenceOff) {
        if (!isDisabled) {
            ctx.beginPath();
            ctx.moveTo(x - radius, y - radius);
            ctx.lineTo(x + radius, y + radius - 4);
            defautStyle.strokeStyle = styles.fillStyle;
            strokeElement(ctx, defautStyle);
            ctx.closePath();
        }

        ctx.beginPath();
        ctx.moveTo(x - radius - 1, y - radius + 1);
        ctx.lineTo(x + radius + 1, y + radius - 1);
        strokeElement(ctx, styles);
        ctx.closePath();
    }
}

/**
 * @description Used to draw a cube
 * @param {CanvasRenderingContext2D} ctx - Canvas Context
 * @param {Number} x - X point to start drawing the cube
 * @param {Number} y - y point to start drawing the cube
 * @param {Number} wx - width of the cube
 * @param {Number} wy - 2d height of the cube
 * @param {Number} h - 3d height
 * @param {Object} styles - Styling options {fillStyle, strokeStyle}
 */
export function drawCube(ctx, x, y, wx, wy, h, styles) {
    ctx.lineJoin = "round";
    ctx.scale(1, 1);

    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x - wx, y - wx * 0.5);
    ctx.lineTo(x - wx, y - h - wx * 0.5);
    ctx.lineTo(x, y - h * 1);
    ctx.closePath();
    fillElement(ctx, styles);
    strokeElement(ctx, styles);

    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x + wy, y - wy * 0.5);
    ctx.lineTo(x + wy, y - h - wy * 0.5);
    ctx.lineTo(x, y - h * 1);

    ctx.moveTo(x, y - h);
    ctx.lineTo(x - wx, y - h - wx * 0.5);
    ctx.lineTo(x - wx + wy, y - h - (wx * 0.5 + wy * 0.5));
    ctx.lineTo(x + wy, y - h - wy * 0.5);
    ctx.closePath();
    strokeElement(ctx, styles);
}

/**
 * @description Used to draw a arrow inside start external plugin
 * @param {Number} x - X point to start drawing the arrow
 * @param {Number} y - y point to start drawing the arrow
 * @param {Number} width - width of the arrow
 * @param {Number} height - height of the arrow
 * @param {Object} styles - Styling options { lineWidth, strokeStyle }
 * @param {CanvasRenderingContext2D} ctx - Canvas 2d Context
 * @param {Number} widthElem - arrow width
 */
export function drawArrowElem(x, y, width, height, styles, ctx, widthElem) {
    const arrowPadding = 2;
    ctx.beginPath();
    // column
    ctx.moveTo(x + width / 2, y + height / 2 - widthElem / 2 - arrowPadding);
    ctx.lineTo(x + width / 2, y + +height / 2 + widthElem / 2 - arrowPadding);
    // row
    ctx.moveTo(x + width / 2 - widthElem / 2, y + height / 2 + widthElem / 2);
    ctx.lineTo(x + width / 2 + widthElem / 2, y + height / 2 + widthElem / 2);

    // left arrow side
    ctx.moveTo(x + width / 2 - widthElem / 2, y + height / 2 + arrowPadding - widthElem / 2);
    ctx.lineTo(x + width / 2, y + height / 2 + arrowPadding);

    // right arrow side
    ctx.moveTo(x + width / 2, y + height / 2 + arrowPadding);
    ctx.lineTo(x + width / 2 + widthElem / 2, y + height / 2 + arrowPadding - widthElem / 2);
    if (styles.lineWidth) {
        ctx.lineWidth = styles.lineWidth;
    }
    strokeElement(ctx, styles);
    ctx.closePath();
}
/**
 * @description Draw a X shape inside exclusive gateway plugin
 * @param {Number} x - X point to start drawing the arrow
 * @param {Number} y - y point to start drawing the arrow
 * @param {Number} width - width
 * @param {Number} height - height
 * @param {Object} styles - Styling options { lineWidth, strokeStyle }
 * @param {CanvasRenderingContext2D} ctx - Canvas 2d Context
 */
export function drawXShape(x, y, width, height, styles, ctx) {
    ctx.beginPath();

    ctx.moveTo(x - width, y - height);
    ctx.lineTo(x + width, y + height);
    ctx.stroke();

    ctx.moveTo(x + width, y - height);
    ctx.lineTo(x - width, y + height);
    if (styles.lineWidth) {
        ctx.lineWidth = styles.lineWidth;
    }
    strokeElement(ctx, styles);
}
/**
 * @description Draw a plus shape inside parallel gateway plugin
 * @param {Number} x - X point to start drawing the arrow
 * @param {Number} y - y point to start drawing the arrow
 * @param {Number} width - width
 * @param {Number} height - height
 * @param {Object} styles - Styling options { lineWidth, strokeStyle }
 * @param {Number} widthElem - parent element width
 * @param {CanvasRenderingContext2D} ctx - Canvas 2d Context
 * @returns void
 */
export function drawPlusShape(x, y, width, height, styles, ctx) {
    ctx.beginPath();

    ctx.moveTo(x, y - height);
    ctx.lineTo(x, y + height);
    ctx.stroke();

    ctx.moveTo(x - width, y);
    ctx.lineTo(x + width, y);
    if (styles.lineWidth) {
        ctx.lineWidth = styles.lineWidth;
    }
    strokeElement(ctx, styles);
    ctx.closePath();
}

/**
 * @param {CanvasRenderingContext2D} ctx - Canvas Context
 * @param {Object} styles - Object containing fillStyle property that has the color used for ctx stroke method
 * @returns void
 */
export function fillElement(ctx, styles) {
    if (ctx && styles && styles.fillStyle) {
        ctx.fillStyle = styles.fillStyle;
        ctx.fill();
    }
}
/**
 * @param {CanvasRenderingContext2D} ctx - Canvas Context
 * @param {Object} styles - Object containing strokeStyle property that has the color used for ctx stroke method
 * @returns void
 */
export function strokeElement(ctx, styles) {
    if (ctx && styles && styles.strokeStyle) {
        const backup = ctx.strokeStyle;
        ctx.strokeStyle = styles.strokeStyle;
        ctx.stroke();
        ctx.strokeStyle = backup;
    }
}

/**
 * @description Adjusts the coordonates taking into consideration screen pixel density, diagram size relative to browser tab, zoom scale factor.
 * @param {FabricDiagram} fabricDiagram instance
 * @param {Object} coords  - {x,y}  object containing mouse position relative to the window
 * @returns {Object} Adjusted coordonates relative to the canvas context. {x,y}
 */
export function getMousePositionRelativeToCanvasContext(fabricDiagram, coords) {
    const parentSize = getParentSize(fabricDiagram.config.containerID);
    // Calculate the percentage of x and y relative to the parent div
    const xPercentage = (coords.x / parentSize.width) * 100;
    const yPercentage = (coords.y / parentSize.height) * 100;
    // Adjusted to window.devicelPixelRatio
    const camera = fabricDiagram.diagramCamera;
    const scaleFactor = getScaleFactor(camera);
    const adjustedParentSize = { width: parentSize.width * camera.transformScale, height: parentSize.height * camera.transformScale };
    const adjustedX = (xPercentage * adjustedParentSize.width) / 100;
    const adjustedY = (yPercentage * adjustedParentSize.height) / 100;
    // calculate X by adding the adjustedX with the offset
    return {
        x: (adjustedX + camera.scrollbars.left) / scaleFactor,
        y: (adjustedY + camera.scrollbars.top) / scaleFactor
    };
}
/**
 * @description Returns the camera scale factor
 * @param {DiagramCamera} camera
 * @returns {Float} Scale factor on canvas
 */
export function getScaleFactor(camera) {
    return camera.getTransformScaleFactor();
}
/**
 * @description update transformScale && transformScaleFactor
 * @param {FabricDiagram} fabricDiagram instance
 * @param {Float} data - scale factor of the camera
 */
export function updateScaleFactor(fabricDiagram, data) {
    const camera = fabricDiagram.diagramCamera;
    camera.updateTransformScaleFactor(data);
}
